import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";

import { MegaphoneIcon } from "../../icons/MegaphoneIcon/MegaphoneIcon";
import { SendIcon } from "../../icons/SendIcon/SendIcon";
import { splitDateTime } from "../../services/DateTimeService";
import { RootState } from "../../store";
import { Comment } from "../../types/comment";

import styles from "./comments.module.css";

type CommentsProps = {
  comments: Comment[];
  processedBy?: any;
  processedAt?: string;
  addComment: (content: string) => void;
  loading?: boolean;
};

export const Comments = ({
  comments,
  processedBy,
  processedAt,
  addComment,
  loading,
}: CommentsProps) => {
  const { t } = useTranslation();

  const [text, setText] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { date, time } = splitDateTime(processedAt, true);

  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );

  const commentsListRef = useRef<HTMLDivElement>(null);

  const handleAddComment = () => {
    if (text.trim()) {
      addComment(text.trim());
      setText("");
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setText(value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "45px";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    setIsExpanded(value.length > 145);
  };

  const commentsBefore = comments.filter(
    (comment) =>
      processedAt && new Date(comment.createdAt) <= new Date(processedAt)
  );
  const commentsAfter = comments.filter(
    (comment) =>
      processedAt && new Date(comment.createdAt) > new Date(processedAt)
  );

  useEffect(() => {
    if (commentsListRef.current) {
      commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
    }
  }, [comments]);

  const renderComment = (comment: Comment, index: number) => {
    if (comment.isSystem === true && comment.additionalData) {
      const { date, time } = splitDateTime(
        comment.additionalData.timestamp,
        true
      );
      return (
        <div key={index} className={styles["processed-info"]}>
          <p>
            {`${comment?.additionalData.from.name || "N/A"}`} teilte den Mangel
            mit{" "}
            <span
              style={{ fontWeight: 700 }}
            >{`${comment?.additionalData.to.email}`}</span>{" "}
            {`[${date}, ${time}]`}
          </p>
        </div>
      );
    }

    return (
      <div key={index} className={styles["comment"]}>
        <div className={styles["comment__by"]}>
          <p className={styles["comment__author"]}>{comment.createdBy.name}</p>
          <p className={styles["comment__content"]}>
            {`[${splitDateTime(comment.createdAt, true).date}, ${
              splitDateTime(comment.createdAt, true).time
            }]:`}
          </p>
        </div>
        <p className={styles["comment__content"]}>{comment.content}</p>
      </div>
    );
  };

  return (
    <div className={styles["comments"]}>
      <div className={styles["comments__header"]}>
        <p className={styles["comments__title"]}>{t("Comments")}</p>
        <MegaphoneIcon color="transparent" />
      </div>

      <div className={styles["comments__list"]} ref={commentsListRef}>
        {loading ? (
          <ClipLoader color="#11A8B8" />
        ) : comments.length > 0 ? (
          <>
            {processedAt && processedBy ? (
              <>
                {commentsBefore.map((comment, index) =>
                  renderComment(comment, index)
                )}

                <div className={styles["processed-info"]}>
                  <p>
                    {t("DeficiencyClosedBy")}
                    <span style={{ fontWeight: 700 }}>
                      {`${processedBy?.user || "N/A"}`}
                    </span>{" "}
                    {`[${date}, ${time}]`}
                  </p>
                </div>

                {commentsAfter.map((comment, index) =>
                  renderComment(comment, index)
                )}
              </>
            ) : (
              comments.map((comment, index) => renderComment(comment, index))
            )}
          </>
        ) : (
          <span className={styles["no-comments"]}>{t("NoCommentsYet")}</span>
        )}
      </div>

      {isCustomerAdmin && (
        <div
          className={`${styles["comment-box"]} ${
            isExpanded ? styles["expanded"] : styles["not-expanded"]
          }`}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            placeholder={t("AddComment") || "Write a comment..."}
            className={styles["textarea"]}
          />
          <button
            className={`${styles["comment-button"]} ${
              text ? styles["filled"] : ""
            }`}
            onClick={handleAddComment}
          >
            Kommentieren <SendIcon />
          </button>
        </div>
      )}
    </div>
  );
};
